import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { notifyPdfNoStamp } from "@/lib/printNotice";
import { printerDisplayName } from "@/lib/printerMap";
import { Printer, FileText, Tag, CheckCircle2, AlertTriangle, Copy } from "lucide-react";
import { printZplBatch, checkBrowserPrint, PrintAborted, ZEBRA_FLUSH_HINT } from "@/lib/zebra";
import { usePrintJob } from "@/lib/usePrintJob";
import { usePrintConfirm } from "@/lib/usePrintConfirm";
import { PrintStopBanner } from "@/components/PrintStopBanner";
import { ReprintReasonPicker } from "@/components/ReprintReasonPicker";
import { reasonLabel } from "@/lib/reprintReasons";
import { mediaLabelOf } from "@/lib/tagMedia";
import { MediaPicker } from "@/components/MediaPicker";

// Part-tag printing on the SAME concept as location / Make-Tags: Zebra Browser Print
// (real ZPL), a confirm dialog, a floating STOP, a copies selector, and a PDF fallback.
// Replaces the old browser window.print() path that never reached the Zebra.
export function PartTagPrintDialog({ part, open, onClose, canReprint }) {
  const [labelData, setLabelData] = useState(null);
  const [bp, setBp] = useState(null);
  const [copies, setCopies] = useState(1);
  const [media, setMedia] = useState("sticker");   // media is independent of the number
  const [reprintOpen, setReprintOpen] = useState(false);
  const printJob = usePrintJob();
  const { printConfirmDialog, confirmAndRun } = usePrintConfirm();

  const recheck = () => checkBrowserPrint().then(setBp);

  useEffect(() => {
    if (open && part?.id) {
      setLabelData(null); setCopies(1); setBp(null);
      api.get(`/parts/${part.id}/label`).then(({ data }) => setLabelData(data))
        .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
      recheck();
    }
  }, [open, part?.id]);

  const runZpl = async (pm = media) => {
    const pmLabel = mediaLabelOf(pm);
    const tid = toast.loading("Preparing tag…");
    try {
      const { data } = await api.get(`/parts/${part.id}/label-zpl`, { params: { media: pm } });
      const list = Array.from({ length: copies }, () => data.zpl);
      const signal = printJob.start(`Tag ${part.short_code}`, list.length);
      try {
        const dev = await printZplBatch(list, (done, total) => {
          printJob.progress(done, total);
          toast.loading(`Printing ${done}/${total}…`, { id: tid });
        }, 1500, signal, pm);
        toast.success(`Sent ${list.length} ${pmLabel}${list.length > 1 ? "s" : ""} for ${part.short_code} to ${printerDisplayName(dev)}.`, { id: tid });
      } catch (e) {
        if (e instanceof PrintAborted || e.aborted) {
          toast.error(`Stopped after ${e.done ?? 0} of ${e.total ?? list.length}. ${ZEBRA_FLUSH_HINT}`, { id: tid, duration: 12000 });
        } else {
          toast.error(`Zebra Browser Print not reachable — use the PDF fallback. (${e.message})`, { id: tid });
        }
      } finally { printJob.end(); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail), { id: tid }); }
  };

  const doPrint = (pm = media) => {
    if (!part) return;
    confirmAndRun({
      title: `Print tag ${part.short_code}`, labelCount: 1, copies, alreadyPrinted: 0,
      noun: "part tags", hideSplit: true, mediaType: pm,
      note: `Prints ${copies} ${mediaLabelOf(pm)}${copies > 1 ? "s" : ""} for ${part.short_code} on the Zebra. Same number — the media is just how it attaches.`,
    }, () => runZpl(pm));
  };

  const openPdf = async () => {
    try {
      const res = await api.get(`/parts/${part.id}/label-pdf`, { params: { media }, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
      notifyPdfNoStamp();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doReprint = async (reason, text, printMedia) => {
    const pm = printMedia || media;
    let why = reasonLabel(reason) + (text ? `: ${text}` : "");
    if (reason === "wrong_media" && pm !== media) why += ` → reprinted as ${mediaLabelOf(pm)}`;
    setReprintOpen(false);
    try {
      await api.post(`/parts/${part.id}/reprint-tag`, { reason: why });
      toast.success("Reprint logged — same record");
      if (pm !== media) setMedia(pm);
      doPrint(pm);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="rounded-lg border-slate-200 sm:max-w-sm" data-testid="part-label-dialog">
          <DialogHeader><DialogTitle className="font-display">Part tag</DialogTitle></DialogHeader>

          <div data-testid="part-bp-status" className={`flex items-center gap-2 text-xs font-mono rounded-lg px-3 py-2 border ${bp?.ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
            {bp?.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {bp == null ? "Checking Zebra Browser Print…" : bp.ok ? `Printer connected · ${bp.count} unit${bp.count === 1 ? "" : "s"} (destination is shown when you confirm)` : "Zebra Browser Print not detected — PDF still works."}
            <button data-testid="part-bp-recheck" onClick={recheck} className="ml-auto underline">Recheck</button>
          </div>

          {!labelData ? <div className="p-6 text-center font-mono text-xs text-slate-400">LOADING…</div> : (
            <div className="text-center" id="part-label-preview">
              <div className="text-3xl font-display font-black tracking-tighter">{labelData.short_code}</div>
              <div className="text-sm text-slate-600">{labelData.name} · {labelData.brand}</div>
              <img src={labelData.qr_code} alt="qr" className="w-40 h-40 mx-auto my-2" data-testid="part-qr-img" />
              <img src={labelData.barcode} alt="barcode" className="h-12 mx-auto" data-testid="part-barcode-img" />
            </div>
          )}

          <div className="flex items-center gap-2 justify-center flex-wrap">
            <span className="text-xs text-slate-500">Media</span>
            <MediaPicker value={media} onChange={setMedia} testidPrefix="part-media" />
          </div>

          <div className="flex items-center gap-2 justify-center">
            <Copy className="w-3.5 h-3.5 text-slate-400" />
            <Label className="text-xs text-slate-500">Copies</Label>
            <Select value={String(copies)} onValueChange={(v) => setCopies(Number(v))}>
              <SelectTrigger data-testid="part-copies-select" className="h-8 w-16 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1" data-testid="part-copies-1">1</SelectItem>
                <SelectItem value="2" data-testid="part-copies-2">2</SelectItem>
                <SelectItem value="3" data-testid="part-copies-3">3</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            {canReprint && part && <Button data-testid="reprint-button" variant="outline" className="rounded-lg border-slate-200" onClick={() => setReprintOpen(true)}><Tag className="w-4 h-4 mr-1" />Reprint</Button>}
            <Button data-testid="part-pdf-button" variant="outline" className="rounded-lg border-slate-200" onClick={openPdf}><FileText className="w-4 h-4 mr-1" />PDF</Button>
            <Button data-testid="print-label-button" onClick={doPrint} className="rounded-lg bg-[#1F1F1F] text-white"><Printer className="w-4 h-4 mr-1" />Print (ZPL)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {printConfirmDialog}
      <PrintStopBanner job={printJob.job} onStop={printJob.stop} />

      <ReprintReasonPicker open={reprintOpen} onOpenChange={setReprintOpen}
        count={copies} currentMedia={media} onConfirm={doReprint} />
    </>
  );
}
